import * as CubeTree from '../../../cubeTree/index';
import * as UIToolkit from '../../../UIToolkit/index';
import CodiSystem from '../../../codiSystem/index';
import Utils from '../../../utils/util';
import Config from '../../../config/index';
import { getBeamLayerInfo } from './beamLayerInfo';

const defaultLayerHeight: PhysicsMeasurement = Config.get({
    moduleName: 'layer',
    key: 'defaultLayerHeight'
});
const defaultModuleHeight: PhysicsMeasurement = Config.get({
    moduleName: 'layer',
    key: 'defaultModuleHeight'
});

function layerAboveOneInsertInfo(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);

    // 新增顶板, 下方是横杆、顶板
    if (CubeTree.isRoofCubeData(cubeData)
        && (CubeTree.isBeam(layer)
            || CubeTree.isRoof(layer))) {
        return addRoofBottomIsBeamOrRoof(cubeData, insertTarget);
    }

    // 新增顶板, 下方是普通层板
    if (CubeTree.isRoofCubeData(cubeData)) {
        return addRoofBottomIsNormalLayer(cubeData, insertTarget);
    }

    // 新增是横杆，下方是横杆、顶板
    if ((CubeTree.isBeam(layer) || CubeTree.isRoof(layer))
        && CubeTree.isBeamCubeData(cubeData)) {
        return addBeamBottomIsBeamOrRoof(cubeData, insertTarget, extendParams);
    }

    // 新增是横杆，下方是普通层板，离地高度为横杆层高+下方层板层高
    if (CubeTree.isBeamCubeData(cubeData)) {
        return addBeamBottomIsNormalLayer(cubeData, insertTarget, extendParams);
    }

    // 新增为普通层板，下方是横杆、顶板
    if (CubeTree.isBeam(layer) || CubeTree.isRoof(layer)) {
        return addLayerBottomIsBeamOrRoof(cubeData, insertTarget, extendParams);
    }

    // 新增为普通层板，下方是普通层板
    return addLayerBottomIsNormalLayer(cubeData, insertTarget, extendParams);
}

function addRoofBottomIsBeamOrRoof(cubeData: FreeObjMap, insertTarget: CubeId): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(topLine, defaultLayerHeight, '+');

    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bottom
        }
    };
}

function addRoofBottomIsNormalLayer(cubeData: FreeObjMap, insertTarget: CubeId): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let layerUsedSpaceHeight: PhysicsMeasurement = UIToolkit.layerSpaceHeightUsed(layer);

    if (layerUsedSpaceHeight <= 0) {
        layerUsedSpaceHeight = defaultLayerHeight;
    }

    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(topLine, layerUsedSpaceHeight, '+');

    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bottom
        }
    };
}

function addBeamBottomIsBeamOrRoof(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let bottom: PhysicsMeasurement = 0;
    let beamInfo: BeamLayerInfo = getBeamLayerInfo(cubeData);
    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');

    if (Utils.isExist(extendParams.spaceHeightUsed)) {
        if (extendParams.spaceHeightUsed <= beamInfo.moduleHeight) {
            bottom = Utils.math(topLine, defaultLayerHeight, '+');
        }
        else {
            bottom = Utils.math(topLine, extendParams.spaceHeightUsed, '+');
            bottom = Utils.math(bottom, beamInfo.moduleHeight, '-');
        }
    }
    else {
        if (beamInfo.layerHeight <= beamInfo.moduleHeight) {
            bottom = Utils.math(topLine, defaultLayerHeight, '+');
        }
        else {
            bottom = Utils.math(topLine, beamInfo.layerHeight, '+');
            bottom = Utils.math(bottom, beamInfo.moduleHeight, '-');
        }
    }

    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bottom
        }
    };
}

function addBeamBottomIsNormalLayer(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let beamInfo: BeamLayerInfo = getBeamLayerInfo(cubeData);
    let layerUsedSpaceHeight: PhysicsMeasurement = UIToolkit.layerSpaceHeightUsed(layer);

    if (layerUsedSpaceHeight <= 0) {
        layerUsedSpaceHeight = defaultLayerHeight;
    }

    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');
    let bottom: PhysicsMeasurement = Utils.math(topLine, layerUsedSpaceHeight, '+');
    let beamLayerHeight: PhysicsMeasurement = 0;

    if (Utils.isExist(extendParams.spaceHeightUsed)) {
        if (extendParams.spaceHeightUsed <= beamInfo.moduleHeight) {
            beamLayerHeight = defaultLayerHeight;
        }
        else {
            beamLayerHeight = Utils.math(extendParams.spaceHeightUsed, beamInfo.moduleHeight, '-');
        }
    }
    else {
        if (beamInfo.layerHeight <= beamInfo.moduleHeight) {
            beamLayerHeight = defaultLayerHeight;
        }
        else {
            beamLayerHeight = Utils.math(beamInfo.layerHeight, beamInfo.moduleHeight, '-');
        }
    }

    bottom = Utils.math(bottom, beamLayerHeight, '+');
    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bottom
        }
    };
}

function addLayerBottomIsBeamOrRoof(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');
    let bootom: PhysicsMeasurement = Utils.math(topLine, defaultLayerHeight, '+');

    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bootom
        }
    };
}

function addLayerBottomIsNormalLayer(cubeData: FreeObjMap, insertTarget: CubeId, extendParams: FreeObjMap): InsertInfo {
    let layer: CubeId = CubeTree.findTheFirstChildCubeId(insertTarget);
    let layerPhysicsCoordinate: PhysicsCoordinate = CodiSystem.getPhysicsCoordinate(layer);
    let topLine: PhysicsMeasurement = Utils.math(layerPhysicsCoordinate.bottom, layerPhysicsCoordinate.height, '+');
    let layerUsedSpaceHeight: PhysicsMeasurement = UIToolkit.layerSpaceHeightUsed(layer);

    if (layerUsedSpaceHeight <= 0) {
        layerUsedSpaceHeight = defaultLayerHeight;
    }

    let bootom: PhysicsMeasurement = Utils.math(topLine, layerUsedSpaceHeight, '+');
    return {
        insertData: cubeData,
        parentId: insertTarget,
        index: 0,
        targetId: insertTarget,
        physicsRelativePosition: {
            left: 0,
            bottom: bootom
        }
    };
}

export {
    layerAboveOneInsertInfo
};
